import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material';
import AppTextInput from 'components/FormElements/AppTextInput';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import Table from 'components/Table';
import commonColumns from './commonColumns';
import { NumericFormat } from 'react-number-format';
import { FileUploadButton } from 'components/FileUploadButton';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { calculateWithdrawalFee } from 'further-ui/utils';
import { InfoOutlined } from '@mui/icons-material';
import trpcApi, { ApiResponses } from 'lib/trpcClient';
import { round } from 'lodash';
import { useNotification } from 'hooks/ui/useNotification';
import { captureException } from '@sentry/react';
import { CalendarDay } from 'further-ui/utils';

const StyledTableWrapper = styled(Box)(() => ({
  '& td.narrow-column, & th.narrow-column': {
    width: '150px',
    minWidth: '150px',
    maxWidth: '150px',
  },
}));

type InvestorDetail = ApiResponses['investor']['getInvestor'];
type ValidateIndividualWithdrawalMutation = ReturnType<
  typeof trpcApi.withdrawals.validateIndividualWithdrawal.useMutation
>;

type Props = {
  firmId: string;
  investorId: string | null;
  validateIndividualWithdrawalMutation: ValidateIndividualWithdrawalMutation;
  addInvestorToWithdrawalQueue: (
    investor: InvestorDetail,
    withdrawalDetails: {
      netWithdrawalAmount: number;
      totalWithdrawal: number;
      withdrawalFee: string;
      withdrawalDate: Date;
      note: string;
      attachment?: {
        key: string;
      };
      maximumWithdrawable?: number;
    },
  ) => void;
  queuedWithdrawals: Array<{ _id: string }>;
};

const InvestorWithdrawalForm: React.FC<Props> = ({
  investorId,
  validateIndividualWithdrawalMutation,
  addInvestorToWithdrawalQueue,
  queuedWithdrawals,
  firmId,
}) => {
  const [investorDetail, setInvestorDetail] = useState<
    InvestorDetail | undefined
  >();
  const [amountToWithdraw, setAmountToWithdraw] = useState('0');
  const [withdrawalFee, setWithdrawalFee] = useState('0');
  const [withdrawalDate, setWithdrawalDate] = useState<Date | null>(new Date());
  const [amountToWithdrawError, setAmountToWithdrawError] = useState('');
  const [withdrawalFeeError, setWithdrawalFeeError] = useState('');
  const [note, setNote] = useState('');
  const [attachment, setAttachment] = useState();
  const [attachmentUploadError, setAttachmentUploadError] = useState();
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const { uploadFile } = useFileUpload();
  const { error: showError } = useNotification();

  const { data: firm } = trpcApi.eisFirm.getById.useQuery(
    { firmId: firmId ?? '' },
    {
      enabled: !!firmId,
    },
  );

  const { data, isFetching } = trpcApi.investor.getInvestor.useQuery(
    { investorId: investorId! },
    { enabled: !!investorId },
  );

  useEffect(() => {
    resetForm();
  }, [investorId]);

  useEffect(() => {
    if (data && data._id === investorId) {
      setInvestorDetail(data);
    }
  }, [data, investorId]);

  const resetForm = () => {
    setAmountToWithdraw('0');
    setWithdrawalFee('0');
    setWithdrawalDate(new Date());
    setNote('');
    setAmountToWithdrawError('');
    setWithdrawalFeeError('');
    setInvestorDetail(undefined);
    setAttachment(undefined);
  };

  const totalWithdrawal = round(
    Number(amountToWithdraw || 0) + Number(withdrawalFee || 0),
    2,
  );

  const validate = () => {
    const firmCashBalance = investorDetail?.cashBalance?.cashBalance;

    if (
      Number(amountToWithdraw) > 0 &&
      totalWithdrawal > Number(firmCashBalance)
    ) {
      setAmountToWithdrawError(
        'The total withdrawal must be less than or equal to the available cash balance.',
      );
      return false;
    }
    if (
      Number(amountToWithdraw) > 0 &&
      Number(amountToWithdraw) < Number(withdrawalFee)
    ) {
      setWithdrawalFeeError(
        'Net withdrawal amount must be greater than the withdrawal fee',
      );
      return false;
    }
    if (Number(withdrawalFee) > 0 && Number(amountToWithdraw) <= 0) {
      setWithdrawalFeeError('Fees not allowed for positive adjustments');
      return false;
    }

    return true;
  };

  const onAttachmentUpload = async (file) => {
    if (!file?.name) return;

    setAttachmentUploading(true);

    const { data, error } = await uploadFile(
      file,
      UploadType.WithdrawalAttachments,
      { firmId },
    );
    if (data) {
      // @ts-expect-error
      setAttachment({ key: data.filePath, signedGetUrl: data.url });
      setAttachmentUploading(false);
    } else {
      // @ts-expect-error
      setAttachmentUploadError(error.message);
    }
    setAttachmentUploading(false);
  };

  const onAttachmentDelete = () => {
    setAttachment(undefined);
    setAttachmentUploadError(undefined);
  };

  useEffect(() => {
    const { fee: defaultWithdrawalFee } = calculateWithdrawalFee(
      firm?.withdrawalFees ?? [],
      Number(amountToWithdraw),
    );
    setWithdrawalFee(defaultWithdrawalFee.toString());
  }, [firm?.withdrawalFees, amountToWithdraw]);

  const withdrawalTooltip =
    'Any adjusted withdrawal fee follows the same VAT treatment as the default withdrawal fee (set on your My Firm page). Please enter any adjusted fee figure inclusive of VAT.';

  const loading =
    validateIndividualWithdrawalMutation.isPending ||
    attachmentUploading ||
    isFetching;

  const columns = [
    ...commonColumns(),
    {
      label: 'Net withdrawal amount',
      key: 'netWithdrawalAmount',
      sort: false,
      tdClassname: 'narrow-column',
      render: () => (
        <NumericFormat
          decimalScale={2}
          fixedDecimalScale
          onValueChange={(values) => {
            setAmountToWithdraw(values.value);
            setAmountToWithdrawError('');
          }}
          value={amountToWithdraw}
          prefix="£"
          allowLeadingZeros={false}
          thousandSeparator={true}
          customInput={AppTextInput}
          error={!!amountToWithdrawError}
          helperText={amountToWithdrawError}
          disabled={loading}
        />
      ),
    },
    {
      label: (
        <Box display="flex" alignItems="center" gap="4px">
          <span>Withdrawal fee</span>
          <Tooltip title={withdrawalTooltip}>
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      key: 'withdrawalFee',
      sort: false,
      tdClassname: 'narrow-column',
      render: () => (
        <NumericFormat
          decimalScale={2}
          onValueChange={(values) => {
            setWithdrawalFee(values.value);
            setWithdrawalFeeError('');
          }}
          fixedDecimalScale
          value={withdrawalFee}
          prefix="£"
          allowNegative={false}
          allowLeadingZeros={false}
          thousandSeparator={true}
          customInput={AppTextInput}
          error={!!withdrawalFeeError}
          helperText={withdrawalFeeError}
          disabled={loading}
        />
      ),
    },
    {
      label: 'Total withdrawal',
      key: 'totalWithdrawal',
      sort: false,
      tdClassname: 'narrow-column',
      render: () => (
        <NumericFormat
          decimalScale={2}
          fixedDecimalScale
          value={totalWithdrawal}
          prefix="£"
          allowLeadingZeros={false}
          thousandSeparator={true}
          customInput={AppTextInput}
          disabled
        />
      ),
    },
    {
      label: 'Withdrawal date',
      key: 'withdrawalDate',
      sort: false,
      tdClassname: 'narrow-column',
      render: () => (
        <AppDatePicker
          value={withdrawalDate}
          onChange={(date) => setWithdrawalDate(date)}
          disableFuture
          placeholder="Select withdrawal date"
          disabled={loading}
        />
      ),
    },
    {
      label: 'Notes',
      key: 'note',
      sort: false,
      render: () => (
        <AppTextInput
          onChange={(e) => setNote(e.target.value)}
          value={note}
          disabled={loading}
        />
      ),
    },
    {
      label: 'Attachment',
      key: 'attachment',
      sort: false,
      render: () => (
        <FileUploadButton
          onUpload={onAttachmentUpload}
          onDelete={onAttachmentDelete}
          uploadError={attachmentUploadError ?? null}
          isUploading={attachmentUploading}
          disabled={loading}
        />
      ),
    },
    {
      label: '',
      key: 'actions',
      tdClassname: 'narrow-column',
      sort: false,
      render: () => (
        <Button
          color="primary"
          variant="contained"
          loading={loading}
          onClick={async () => {
            if (!validate()) return;
            if (attachmentUploading) return;
            if (!investorDetail) return;
            if (!withdrawalDate) {
              showError('Withdrawal date is required');
              return;
            }

            try {
              const withdrawalAmount = Number(amountToWithdraw);
              const fee = Number(withdrawalFee);

              const response =
                await validateIndividualWithdrawalMutation.mutateAsync({
                  investorId: investorDetail._id,
                  firmId,
                  amount: withdrawalAmount,
                  withdrawalDate: new CalendarDay(withdrawalDate),
                  withdrawalFee: fee,
                });

              if (!response?.canWithdraw) {
                showError(
                  'This investor has insufficient funds at the withdrawal date. Please check the cash balance and try again.',
                );
                return;
              }

              addInvestorToWithdrawalQueue(investorDetail, {
                netWithdrawalAmount: Number(amountToWithdraw),
                totalWithdrawal,
                withdrawalFee,
                withdrawalDate,
                note,
                attachment,
                maximumWithdrawable: response.maximumWithdrawable,
              });
              resetForm();
            } catch (err) {
              captureException(err);
              showError(
                'An error occurred while validating the withdrawal. Please try again.',
              );
              return;
            }
          }}
        >
          Add request
        </Button>
      ),
    },
  ];

  return (
    <StyledTableWrapper>
      <Table
        loading={isFetching}
        columns={columns}
        tablebody={investorDetail ? [investorDetail] : []}
        emptyMessage={
          queuedWithdrawals.length > 0
            ? 'Select another investor from the list'
            : 'First select an investor from the list'
        }
        variant="nohover"
      />
    </StyledTableWrapper>
  );
};

export default InvestorWithdrawalForm;
